import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Row, Stack } from "react-bootstrap";
import { unsavedChanges } from "../../../App";
import Input from "../../ui/Input";
import SelectObject from "../../ui/SelectObject";
import { NoteType } from "../../../types/notetype";
import { postNoteType } from "../../../services/httpClient";
import Textarea from "../../ui/Textarea";
import CreateNoteTypeFieldCard from "./CreateNoteTypeFieldCard";
import fieldTypes from "../../../data/notetypefieldtypes.json";

interface FieldType {
  key: string;
  name: string;
}

interface NoteTypeState {
  getNoteTypes: any;
  setOrgToSave: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  setShowCreateType: any;
  setIsLoading: any;
  setErrMsg: any;
}

const CreateNoteTypeCard = ({
  getNoteTypes,
  setOrgToSave,
  setShowToast,
  setToastText,
  setToastBody,
  setShowCreateType,
  setIsLoading,
  setErrMsg,
}: NoteTypeState) => {
  const [noteTypeName, setNoteTypeName] = useState<string | undefined>();
  const [fields, setFields] = useState<any[]>([]);
  const [newLabel, setNewLabel] = useState<string>("");
  const [newType, setNewType] = useState<string>("");
  const [enableAddField, setEnableAddField] = useState<boolean>(false);
  const [allowSaveDraft, setAllowSaveDraft] = useState<boolean>(false);
  const [allowPublish, setAllowPublish] = useState<boolean>(false);
  const [numPreviews, setNumPreviews] = useState<number>(0);
  const typesList: FieldType[] = fieldTypes;

  const updateNoteName = (e: any) => {
    setNoteTypeName(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const updateLabel = (e: any) => {
    setNewLabel(e.target.value);

    if (e.target.value && newType) {
      setEnableAddField(true);
    } else {
      setEnableAddField(false);
    }
  };

  const updateType = (e: any) => {
    setNewType(e.target.value);

    if (newLabel && e.target.value && e.target.value !== "0") {
      setEnableAddField(true);
    } else {
      setEnableAddField(false);
    }
  };

  const onClickAddFieldSubmit = (e: any) => {
    e?.preventDefault();
    onClickAddField();
  };

  const onClickAddField = () => {
    const fieldObj: any = { id: crypto.randomUUID(), label: newLabel, type: newType, options: [], preview: false };
    setFields((prev) => [...prev, fieldObj]);
    setNewLabel("");
    setNewType("");
    setEnableAddField(false);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  useEffect(() => {
    const hasName = !!noteTypeName;
    const hasFields = fields.length > 0;
    const emptyDropdown = fields.some((f) => f.type === "dropdown" && f.options.length === 0);

    setAllowSaveDraft(hasName && hasFields);
    setAllowPublish(hasName && hasFields && !emptyDropdown);
  }, [noteTypeName, fields]);

  const onClickSave = (publish = false) => {
    setErrMsg("");
    setIsLoading(true);

    let newType: NoteType = {
      name: noteTypeName,
      type: "custom",
      fields: fields.map((f: any, index: number) => ({
        label: f?.label,
        type: f?.type,
        preview: !!f?.preview,
        order: index + 1,
        ...(f?.type === "dropdown" && {
          options: f?.options?.map((o: any, index: number) => ({
            name: o?.name,
            order: index + 1,
          })),
        }),
      })),
    };

    if (publish) {
      newType = { ...newType, is_published: true };
    }

    postNoteType(newType)
      .then((res: any) => {
        setAllowSaveDraft(false);
        setAllowPublish(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        // trackFeatureEvent("button_click", "create_custom_note_type");
        getNoteTypes(publish);
        setIsLoading(false);
        setShowCreateType(false);
        setShowToast(true);
        setToastText("Note Type Created");
        setToastBody(noteTypeName);
      })
      .catch((err: any) => {
        setIsLoading(false);
        setErrMsg(err?.response?.data?.msg);
      });
  };

  return (
    <>
      <Card className="bg-light mb-3">
        <Card.Body>
          <Row className="mb-2">
            <Col xs={12}>
              <Input label="Note Type Name" type="text" value={noteTypeName} onUpdate={(e: any) => updateNoteName(e)} hasFocus={true} />
            </Col>
          </Row>
          <Row>
            <h6 className="cursor-pointer ms-1">Fields</h6>
          </Row>
          <Row className="mb-2 g-3">
            <Col>
              <Input label="Label" type="text" value={"Household Member"} disabled={true} onUpdate={null} />
            </Col>
            <Col>
              <Input label="Type" type="text" value={"dropdown"} disabled={true} onUpdate={null} />
            </Col>
          </Row>
          <Row className="mb-2 g-3">
            <Col>
              <Input label="Label" type="text" value={"Date"} disabled={true} onUpdate={null} />
            </Col>
            <Col>
              <Input label="Type" type="text" value={"date"} disabled={true} onUpdate={null} />
            </Col>
          </Row>
          {fields.map((f: any, index: number) => (
            <CreateNoteTypeFieldCard
              key={f?.id}
              field={f}
              index={index}
              fields={fields}
              setFields={setFields}
              numPreviews={numPreviews}
              setNumPreviews={setNumPreviews}
            />
          ))}
          <h6 className="cursor-pointer mt-2 ms-1">Create New Field</h6>
          <Form onSubmit={(e: any) => onClickAddFieldSubmit(e)}>
            <Row className="g-3">
              <Col xs={6}>
                <Textarea
                  label="Label"
                  value={newLabel}
                  onUpdate={(e: any) => updateLabel(e)}
                  rows={1}
                  css={""}
                  hasFocus={noteTypeName ? true : false}
                />
              </Col>
              <Col>
                <SelectObject label="Type" value={newType} options={typesList} onUpdate={(e: any) => updateType(e)} />
              </Col>
              <Col xs={"auto"} className="d-flex align-items-center">
                <Button type="submit" className="app-secondary-color button" disabled={!enableAddField} onClick={() => onClickAddField()}>
                  Add
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
      <Row className="mb-5">
        <Col className="text-center">
          <Stack direction="horizontal" gap={3} className="justify-content-center">
            <Button className="button app-secondary-color button" onClick={() => onClickSave(false)} disabled={!allowSaveDraft}>
              Save Draft
            </Button>
            <div className="vr mb-1" />
            <Button className="button app-tertiary-color button" onClick={() => onClickSave(true)} disabled={!allowPublish}>
              Publish
            </Button>
          </Stack>
        </Col>
      </Row>
    </>
  );
};

export default CreateNoteTypeCard;
